import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import DashboardCard, { EmptyState } from './DashboardCard';
import { chartColors, fmtInt, methodColor, methodLabels } from './formatters';

// Optional: map ResultCode values to readable text, e.g. { '5010': 'License expired' }.
export const resultCodeLabels = {};

/** Activation method mix (ActivationType) plus failed attempts by ResultCode. */
export default function ActivationMethodCard({ methods = [], failures = [], sx }) {
  const total = methods.reduce((s, m) => s + m.count, 0);
  const maxFail = Math.max(1, ...failures.map((f) => f.count));

  return (
    <DashboardCard title="Activation method" subtitle="Successful activations by ActivationType" sx={sx}>
      {total === 0 ? (
        <EmptyState />
      ) : (
        <>
          <Box sx={{ display: 'flex', height: 16, borderRadius: 2, overflow: 'hidden', bgcolor: 'action.hover' }} aria-hidden>
            {methods.map((m) => (
              // A 1.5% floor keeps tiny slices visible.
              <Box key={m.type} sx={{ width: `${Math.max((m.count / total) * 100, 1.5)}%`, bgcolor: methodColor(m.type) }} />
            ))}
          </Box>
          <Stack sx={{ gap: 1.5 }}>
            {methods.map((m) => (
              <Stack key={m.type} direction="row" sx={{ alignItems: 'center', gap: 1.25 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', flex: 'none', bgcolor: methodColor(m.type) }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2">{methodLabels[m.type] ?? m.type}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {m.type}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{fmtInt(m.count)}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ width: 52, textAlign: 'right' }}>
                  {((m.count / total) * 100).toFixed(1)}%
                </Typography>
              </Stack>
            ))}
          </Stack>
        </>
      )}

      <Divider />
      <Typography variant="subtitle2" component="h3">Failed attempts by result code</Typography>
      {failures.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No failed attempts in this range</Typography>
      ) : (
        <Stack sx={{ gap: 1 }}>
          {failures.map((f) => (
            <Stack key={f.code} direction="row" sx={{ alignItems: 'center', gap: 1.25 }}>
              <Typography variant="body2" sx={{ minWidth: 48, fontFamily: 'monospace' }} title={resultCodeLabels[f.code]}>
                {f.code}
              </Typography>
              {resultCodeLabels[f.code] && (
                <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 140 }}>
                  {resultCodeLabels[f.code]}
                </Typography>
              )}
              <Box sx={{ flex: 1, height: 8, borderRadius: 1, bgcolor: 'action.hover', overflow: 'hidden' }}>
                <Box sx={{ height: '100%', width: `${(f.count / maxFail) * 100}%`, bgcolor: chartColors.failed }} />
              </Box>
              <Typography variant="body2" sx={{ width: 32, textAlign: 'right', fontWeight: 500 }}>{fmtInt(f.count)}</Typography>
            </Stack>
          ))}
        </Stack>
      )}
      <Typography variant="caption" color="text.secondary">Any ResultCode other than 0 counts as a failure</Typography>
    </DashboardCard>
  );
}
